import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Appearance, Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bank as BankIcon } from '@phosphor-icons/react/dist/ssr/Bank';
import { CreditCard as CreditCardIcon } from '@phosphor-icons/react/dist/ssr/CreditCard';
import { Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr/Plus';
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { CheckCircle as CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle';

import { WarningCircle as WarningIcon } from '@phosphor-icons/react/dist/ssr/WarningCircle';

import { config } from '@/config';
import { toast } from '@/components/core/v2/toaster';
import type { Community } from '@/types/v2/community';
import { useConnectStatus } from '@/hooks/v2/use-connect-status';
import {
  deletePaymentMethod,
  getPaymentMethods,
  getSetupIntent,
  setDefaultPaymentMethod,
  type StoredPaymentMethod,
} from '@/api/payment-methods';

/**
 * Load a Stripe instance scoped to the community's connected account.
 * The result is memoized per account ID so we don't re-instantiate on every render.
 */
const stripeInstanceCache = new Map<string, Promise<Stripe | null>>();

function getStripeForAccount(stripeAccountId?: string | null): Promise<Stripe | null> {
  if (!config.stripe.publishableKey) return Promise.resolve(null);

  const cacheKey = stripeAccountId ?? '__platform__';

  if (!stripeInstanceCache.has(cacheKey)) {
    const opts = stripeAccountId ? { stripeAccount: stripeAccountId } : undefined;
    stripeInstanceCache.set(cacheKey, loadStripe(config.stripe.publishableKey, opts));
  }

  return stripeInstanceCache.get(cacheKey)!;
}

function useStripeAppearance(): Appearance {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return React.useMemo(
    (): Appearance => ({
      theme: isDark ? 'night' : 'stripe',
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorBackground: theme.palette.background.paper,
        colorText: theme.palette.text.primary,
        colorTextSecondary: theme.palette.text.secondary,
        colorDanger: theme.palette.error.main,
        fontFamily: theme.typography.fontFamily as string,
        borderRadius: '8px',
        spacingUnit: '4px',
        colorTextPlaceholder: theme.palette.text.disabled,
      },
      rules: {
        '.Input': {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          boxShadow: 'none',
        },
        '.Input:focus': {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        },
        '.Label': {
          color: theme.palette.text.secondary,
        },
        '.Tab': {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.secondary,
          boxShadow: 'none',
        },
        '.Tab--selected': {
          backgroundColor: theme.palette.action.selected,
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.text.primary,
        },
        '.Tab:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
      },
    }),
    [isDark, theme],
  );
}

function SetupForm({ onSuccess }: { onSuccess: () => void }): React.JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(error.message ?? 'Failed to save payment method.');
    } else {
      toast.success('Payment method saved successfully.');
      onSuccess();
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card', 'us_bank_account'],
          }}
        />
        <Button
          disabled={!stripe || submitting}
          type="submit"
          variant="contained"
          size="small"
          sx={{ alignSelf: 'flex-end' }}
        >
          {submitting ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </Stack>
    </form>
  );
}

function AddPaymentForm({
  communityId,
  onSuccess,
}: {
  communityId: string | number;
  onSuccess: () => void;
}): React.JSX.Element {
  const appearance = useStripeAppearance();
  const { data, isLoading } = useQuery({
    queryKey: ['setup-intent', communityId],
    queryFn: () => getSetupIntent(communityId),
    enabled: !!communityId,
  });

  const [stripePromise, setStripePromise] = React.useState<Promise<Stripe | null> | null>(null);

  React.useEffect(() => {
    if (data?.stripe_account_id) {
      setStripePromise(getStripeForAccount(data.stripe_account_id));
    }
  }, [data?.stripe_account_id]);

  if (isLoading || !data?.client_secret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!stripePromise) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: data.client_secret,
        appearance,
      }}
    >
      <SetupForm onSuccess={onSuccess} />
    </Elements>
  );
}

function PaymentMethodCard({
  pm,
  onDelete,
  onSetDefault,
  deleting,
  settingDefault,
}: {
  pm: StoredPaymentMethod;
  onDelete: (id: string) => void;
  onSetDefault?: (id: string) => void;
  deleting?: boolean;
  settingDefault?: boolean;
}): React.JSX.Element {
  const isBank = pm.type === 'us_bank_account';
  const Icon = isBank ? BankIcon : CreditCardIcon;
  const label = isBank
    ? `${pm.bank_name ?? 'Bank'} ···· ${pm.last4}`
    : `${pm.brand ?? 'Card'} ···· ${pm.last4}`;
  const subtitle = isBank
    ? `${(pm.account_type ?? 'checking').charAt(0).toUpperCase() + (pm.account_type ?? 'checking').slice(1)} account`
    : `Expires ${pm.exp_month}/${pm.exp_year}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        border: '1px solid',
        borderColor: pm.is_default ? 'success.main' : 'divider',
        borderRadius: 2,
        bgcolor: pm.is_default ? 'action.selected' : 'background.paper',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {pm.is_default ? <CheckCircleIcon size={18} /> : <Icon size={18} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }} noWrap>
              {label}
            </Typography>
            {pm.is_default && <Chip label="Default" size="small" color="success" variant="outlined" />}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 1 }}>
        {!pm.is_default && onSetDefault && (
          <Button size="small" variant="text" onClick={() => onSetDefault(pm.id)} disabled={settingDefault || deleting}>
            {settingDefault ? 'Saving...' : 'Make default'}
          </Button>
        )}
        <Tooltip title="Remove">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(pm.id)}
            disabled={deleting || settingDefault}
          >
            <TrashIcon size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

interface PaymentMethodManagerProps {
  communityId: string | number;
  compact?: boolean;
  /**
   * @deprecated Stripe readiness is now resolved live via `useConnectStatus`, so the
   * gating no longer depends on a passed-in community. Accepted for backward
   * compatibility with existing callers.
   */
  community?: Community | null;
}

export function PaymentMethodManager({ communityId, compact }: PaymentMethodManagerProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [settingDefaultId, setSettingDefaultId] = React.useState<string | null>(null);

  const { isReady: stripeReady, paymentsEnabled, isLoading: statusLoading } = useConnectStatus(communityId);

  // If Stripe becomes unready while the add form is open, snap back to the list view.
  React.useEffect(() => {
    if (!stripeReady && showAddForm) setShowAddForm(false);
  }, [stripeReady, showAddForm]);

  const { data, isLoading } = useQuery({
    queryKey: ['payment-methods', communityId],
    queryFn: () => getPaymentMethods(communityId),
    enabled: !!communityId,
  });

  const deleteMutation = useMutation({
    mutationFn: (pmId: string) => deletePaymentMethod(pmId, communityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods', communityId] });
      toast.success('Payment method removed.');
    },
    onError: () => {
      toast.error('Failed to remove payment method.');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (pmId: string) => setDefaultPaymentMethod(pmId, communityId),
    onMutate: (pmId) => setSettingDefaultId(pmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods', communityId] });
      toast.success('Default payment method updated.');
    },
    onError: () => {
      toast.error('Failed to update default payment method.');
    },
    onSettled: () => setSettingDefaultId(null),
  });

  const methods = data?.payment_methods ?? [];

  const handleAddSuccess = () => {
    setShowAddForm(false);
    queryClient.invalidateQueries({ queryKey: ['payment-methods', communityId] });
    queryClient.invalidateQueries({ queryKey: ['setup-intent', communityId] });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Gating is "known" once the live connect-status query resolves. While it's
  // loading we stay non-committal so we don't flash an Add button (or a warning)
  // before we know the community's true Stripe readiness.
  const stripeGatingKnown = !statusLoading;

  return (
    <Stack spacing={2}>
      {!compact && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Payment Methods
        </Typography>
      )}

      {stripeGatingKnown && !stripeReady && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: 'warning.light',
          }}
        >
          <WarningIcon size={20} weight="fill" style={{ flexShrink: 0, marginTop: 2 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {!paymentsEnabled ? 'Payments not available' : 'Payment setup required'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {!paymentsEnabled
                ? 'Your community has not enabled payments yet. Please contact your account administrator.'
                : "Your community's Stripe account needs to finish onboarding before payment methods can be added."}
            </Typography>
          </Box>
        </Box>
      )}

      {methods.map((pm) => (
        <PaymentMethodCard
          key={pm.id}
          pm={pm}
          onDelete={(id) => deleteMutation.mutate(id)}
          onSetDefault={(id) => setDefaultMutation.mutate(id)}
          deleting={deleteMutation.isPending}
          settingDefault={settingDefaultId === pm.id}
        />
      ))}

      {methods.length === 0 && !showAddForm && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <CreditCardIcon size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <Typography variant="body2" color="text.secondary">
            No payment methods on file
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {stripeGatingKnown && !stripeReady
              ? 'Payment methods can be added once your community completes Stripe setup.'
              : 'Add a card or bank account to get started'}
          </Typography>
        </Box>
      )}

      {showAddForm ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
            <Typography variant="subtitle2">Add payment method</Typography>
            <Chip label="Card or Bank" size="small" variant="outlined" />
          </Stack>
          <AddPaymentForm communityId={communityId} onSuccess={handleAddSuccess} />
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setShowAddForm(false)}
            sx={{ mt: 1.5 }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        (!stripeGatingKnown || stripeReady) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CreditCardIcon size={16} />}
            onClick={() => setShowAddForm(true)}
          >
            {methods.length > 0 ? 'Add Another Method' : 'Add Payment Method'}
          </Button>
        )
      )}
    </Stack>
  );
}

interface PaymentMethodSelectorProps {
  communityId: string | number;
  onHasMethod?: (hasMethods: boolean) => void;
  selectedMethodId?: string | null;
  onSelectMethod?: (id: string) => void;
  onDefaultMethodChange?: (method: StoredPaymentMethod | null) => void;
  selectable?: boolean;
  /**
   * @deprecated Stripe readiness is now resolved live via `useConnectStatus`.
   * Accepted for backward compatibility with existing callers.
   */
  community?: Community | null;
}

export function PaymentMethodSelector({
  communityId,
  onHasMethod,
  selectedMethodId,
  onSelectMethod,
  onDefaultMethodChange,
  selectable = true,
}: PaymentMethodSelectorProps): React.JSX.Element {
  const { isReady: stripeReady, isLoading: statusLoading } = useConnectStatus(communityId);
  const stripeGatingKnown = !statusLoading;
  const { data, isLoading } = useQuery({
    queryKey: ['payment-methods', communityId],
    queryFn: () => getPaymentMethods(communityId),
    enabled: !!communityId,
  });

  const methods = data?.payment_methods ?? [];

  React.useEffect(() => {
    onHasMethod?.(methods.length > 0);
  }, [methods.length, onHasMethod]);

  React.useEffect(() => {
    onDefaultMethodChange?.(methods.find((method) => method.is_default) ?? methods[0] ?? null);
  }, [methods, onDefaultMethodChange]);

  // Auto-select the default method when methods load. Fallback to the first method
  // for older accounts that do not have an explicit default yet.
  React.useEffect(() => {
    if (methods.length > 0 && !selectedMethodId && onSelectMethod) {
      onSelectMethod((methods.find((method) => method.is_default) ?? methods[0]).id);
    }
  }, [methods, selectedMethodId, onSelectMethod]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (methods.length === 0) {
    const stripeNotReady = stripeGatingKnown && !stripeReady;
    return (
      <Box
        sx={{
          p: 2.5,
          border: '1px dashed',
          borderColor: 'warning.main',
          borderRadius: 2,
          bgcolor: 'warning.light',
          color: 'text.primary',
          textAlign: 'center',
        }}
      >
        <CreditCardIcon size={28} style={{ marginBottom: 6, opacity: 0.6 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {stripeNotReady ? 'Payments not available' : 'No payment method on file'}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5 }}>
          {stripeNotReady
            ? "Your community hasn't finished setting up online payments yet. Please contact your community manager."
            : 'You need to add a payment method before making a reservation with a deposit.'}
        </Typography>
        {!stripeNotReady && (
          <Button
            size="small"
            variant="contained"
            href="/settings/details/payments"
            startIcon={<CreditCardIcon size={16} />}
          >
            Go to Payment Settings
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {methods.map((pm) => {
        const isBank = pm.type === 'us_bank_account';
        const Icon = isBank ? BankIcon : CreditCardIcon;
        const label = isBank
          ? `${pm.bank_name ?? 'Bank'} ···· ${pm.last4}`
          : `${pm.brand ?? 'Card'} ···· ${pm.last4}`;
        const subtitle = isBank
          ? `${(pm.account_type ?? 'checking').charAt(0).toUpperCase() + (pm.account_type ?? 'checking').slice(1)} account`
          : `Expires ${pm.exp_month}/${pm.exp_year}`;
        const isDefault = Boolean(pm.is_default);
        const isSelected = selectable ? selectedMethodId === pm.id : isDefault;

        return (
          <Box
            key={pm.id}
            onClick={() => {
              if (selectable) onSelectMethod?.(pm.id);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 2,
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: isSelected ? 'action.hover' : 'transparent',
              cursor: selectable && onSelectMethod ? 'pointer' : 'default',
              transition: 'all 0.15s',
              '&:hover': selectable
                ? {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                : undefined,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: isDefault ? 'success.main' : isSelected ? 'primary.main' : 'action.hover',
                color: isSelected ? 'primary.contrastText' : 'text.primary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}
            >
              <Icon size={18} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                noWrap
              >
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {isDefault
                  ? 'Default payment method'
                  : selectable && isSelected
                    ? 'Deposit will be charged to this method'
                    : subtitle}
              </Typography>
            </Box>
            {isDefault ? (
              <Chip label="Default" size="small" color="success" variant="outlined" />
            ) : selectable && isSelected ? (
              <Chip label="Selected" size="small" color="primary" variant="outlined" />
            ) : null}
          </Box>
        );
      })}

      {/* Add another payment method — links to settings. Hidden when Stripe isn't set up. */}
      {(!stripeGatingKnown || stripeReady) && (
        <Box
          component="a"
          href="/settings/details/payments"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            textDecoration: 'none',
            color: 'text.secondary',
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PlusIcon size={18} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit' }}>
              Add another payment method
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Manage cards and bank accounts in your settings
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
